import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { whatsappService } from '../services/whatsappService';
import { setupAgentWithWorkflows } from '../services/agentSetupService';
import { agentsApi } from '../../agents/services/agentsApi';
import { PLAN_OPTIONS, planOptionLabel } from '../../agents/constants/plans';
import { saveAgentConfig, getAgentConfig, updateAgentConfig } from '../../agents/services/agentConfigApi';
import {
  triggerExtraction,
  subscribeToExtraction,
  getLatestExtraction,
  ExtractionProgress,
} from '../services/extractionService';
import { useAuth } from '../../../context/AuthContext';
import {
  Loader2, CheckCircle2, XCircle, RefreshCw, Building2,
  MessageSquareText, ArrowRight, Check, Minus,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { OnboardingStepIndicator } from './OnboardingStepIndicator';
import { ExtractionProgressBar } from './ExtractionProgressBar';
import { BriefDocumentUpload } from './BriefDocumentUpload';
import type { BriefDocumentsData, DocumentUpload, OnboardingData } from '../types/onboarding';

interface QRDisplayModalProps {
  open: boolean;
  onClose: () => void;
  qrCode: string;
  instanceName: string;
  connectionId: string;
  onSuccess: () => void;
}

type ConnectionStatus =
  | 'waiting'
  | 'success'
  | 'business_setup'
  | 'setting_up'
  | 'setup_complete'
  | 'extraction_consent'
  | 'brief_questions'
  | 'onboarding_complete'
  | 'error'
  | 'qr_expired';

const POLL_INTERVAL = 4000;
const MAX_RETRIES = 3;

const ONBOARDING_STEPS = [
  { label: 'Conexión' },
  { label: 'Negocio' },
  { label: 'Historial' },
  { label: 'Documentos' },
];

function getStepIndex(status: ConnectionStatus): number {
  switch (status) {
    case 'waiting': return 0;
    case 'success':
    case 'business_setup':
    case 'setting_up': return 1;
    case 'extraction_consent': return 2;
    case 'brief_questions': return 3;
    case 'onboarding_complete': return 4;
    default: return 0;
  }
}

function getCompletedSteps(status: ConnectionStatus): boolean[] {
  const idx = getStepIndex(status);
  return ONBOARDING_STEPS.map((_, i) => i < idx);
}

export const QRDisplayModal: React.FC<QRDisplayModalProps> = ({
  open,
  onClose,
  qrCode: initialQR,
  instanceName,
  connectionId,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // QR & connection state
  const [qrCode, setQrCode] = useState(initialQR);
  const [status, setStatus] = useState<ConnectionStatus>('waiting');
  const [errorMessage, setErrorMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [businessName, setBusinessName] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [escalationPhone, setEscalationPhone] = useState('');
  const [connectedPhone, setConnectedPhone] = useState('');
  const cancelledRef = useRef(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startingCountRef = useRef(0);
  const MAX_STARTING_POLLS = 15; // ~60s of STARTING before auto-restart

  // Onboarding state
  const [agentId, setAgentId] = useState('');
  const [configId, setConfigId] = useState('');
  const [extractionProgress, setExtractionProgress] = useState<ExtractionProgress | null>(null);
  const [extractionStatus, setExtractionStatus] = useState<string | null>(null);
  const [extractionRunning, setExtractionRunning] = useState(false);
  const [briefDocuments, setBriefDocuments] = useState<BriefDocumentsData>({ documents: [] });
  const extractionUnsubRef = useRef<(() => void) | null>(null);

  // Cleanup polling
  const cleanup = useCallback(() => {
    cancelledRef.current = true;
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Cleanup extraction subscription on unmount
  useEffect(() => {
    return () => {
      extractionUnsubRef.current?.();
    };
  }, []);

  // --- Connection handlers (unchanged logic) ---

  const handleConnectionSuccess = useCallback(async (phoneNumber: string) => {
    try {
      await whatsappService.updateConnectionStatus(connectionId, 'connected', {
        connected_at: new Date().toISOString(),
        numero_telefono: phoneNumber,
      });
      await whatsappService.incrementCounter();
      setConnectedPhone(phoneNumber);
      setStatus('business_setup');
    } catch (err: unknown) {
      console.error('[QRDisplayModal] Error guardando conexión:', err);
      const isDuplicate = err && typeof err === 'object' && 'code' in err && (err as { code: string }).code === '23505';
      if (isDuplicate) {
        setStatus('error');
        setErrorMessage(`El número +${phoneNumber} ya está conectado en otra instancia. Elimina la conexión existente primero.`);
      } else {
        setStatus('error');
        setErrorMessage('Error al guardar la conexión. Intenta de nuevo.');
      }
      try {
        await whatsappService.deleteConnection(connectionId, instanceName);
      } catch (e) {
        console.error('Error limpiando conexión:', e);
      }
    }
  }, [connectionId, instanceName]);

  // --- Setup agent → transition to onboarding ---

  // Número de escalamiento humano: solo dígitos (sin +, espacios ni guiones)
  const escalationDigits = escalationPhone.replace(/\D/g, '');
  const escalationValid = escalationDigits.length >= 10;

  const handleSetupAgent = async () => {
    if (!businessName.trim() || !escalationValid) return;
    if (!user?.id) {
      setStatus('error');
      setErrorMessage('No se pudo obtener el usuario autenticado.');
      return;
    }

    setStatus('setting_up');

    try {
      const result = await setupAgentWithWorkflows(
        businessName.trim(),
        instanceName,
        connectedPhone,
        user.id,
        escalationDigits
      );

      if (result.success) {
        setAgentId(result.agentId);

        // Guardar el plan seleccionado (best-effort, no bloquea el onboarding)
        if (selectedPlan) {
          try {
            await agentsApi.updateAgent(result.agentId, { plan: selectedPlan });
          } catch (e) {
            console.error('[QRDisplayModal] Error guardando plan:', e);
          }
        }

        // Initialize onboarding_data in agent_config
        const onboardingData: OnboardingData = {
          status: 'in_progress',
          current_step: 'extraction',
          extraction_started: false,
          brief_documents: { documents: [] },
        };

        try {
          const config = await getAgentConfig(result.agentId);
          if (config) {
            setConfigId(config.id);
            await updateAgentConfig(config.id, { onboarding_data: onboardingData } as any);
          } else {
            const newConfig = await saveAgentConfig({
              agent_id: result.agentId,
              system_prompt: '',
              max_mensajes: 1,
              tiempo_agrupacion: 5,
              onboarding_data: onboardingData,
            } as any);
            if (newConfig) setConfigId(newConfig.id);
          }
        } catch (e) {
          console.error('Error saving onboarding state:', e);
        }

        toast.success('Agente creado exitosamente');
        setStatus('extraction_consent');
      } else {
        setStatus('error');
        setErrorMessage('Error al configurar el agente. Revisa el flujo en n8n.');
      }
    } catch (err) {
      console.error('[QRDisplayModal] Error configurando agente:', err);
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Error al configurar el agente.');
    }
  };

  const handleSkipSetup = () => {
    onSuccess();
    onClose();
  };

  // --- Extraction handlers ---

  const startExtraction = async () => {
    if (!user?.id || !agentId || !instanceName) return;

    setExtractionRunning(true);
    setExtractionStatus('pending');

    try {
      const { extraction_id } = await triggerExtraction({
        userId: user.id,
        agentId,
        sessionName: instanceName,
      });

      // Save extraction_started in onboarding_data
      if (configId) {
        try {
          await saveOnboardingState('brief');
        } catch (e) {
          console.error('Error saving onboarding state:', e);
        }
      }

      const unsubscribe = subscribeToExtraction(extraction_id, (data) => {
        setExtractionProgress(data);
        setExtractionStatus(data.status);
        if (data.status === 'completed' || data.status === 'failed') {
          setExtractionRunning(false);
          unsubscribe();
          if (data.status === 'completed') {
            toast.success('Historial extraido exitosamente');
          } else {
            toast.error(data.error_message || 'Error en la extracción');
          }
        }
      });

      extractionUnsubRef.current = unsubscribe;

      // Move to brief questions in parallel
      setStatus('brief_questions');
    } catch (error: any) {
      console.error('Error triggering extraction:', error);
      toast.error(error.message || 'Error al iniciar la extracción');
      setExtractionRunning(false);
      setExtractionStatus(null);
      // Still move forward even if extraction fails
      setStatus('brief_questions');
    }
  };

  const handleSkipExtraction = () => {
    setStatus('brief_questions');
    saveOnboardingState('brief');
  };

  // --- Brief & Calendar handlers ---

  const handleBriefNext = async () => {
    // Compile extracted text from all processed documents → save to documentos_negocio
    const allText = briefDocuments.documents
      .filter((d) => d.status === 'done')
      .map((d) => `--- ${d.fileName} ---\n${d.extractedText || d.summary || ''}`)
      .join('\n\n');

    if (allText.trim() && configId) {
      try {
        await updateAgentConfig(configId, { documentos_negocio: allText } as any);
      } catch (e) {
        console.error('Error saving documentos_negocio:', e);
      }
    }

    setStatus('onboarding_complete');
    saveOnboardingState('complete');
  };

  // --- Persist onboarding state ---

  const saveOnboardingState = async (
    step?: OnboardingData['current_step'],
  ) => {
    if (!configId) return;
    const resolvedStep = step || (
      status === 'brief_questions' ? 'brief'
      : 'extraction'
    ) as OnboardingData['current_step'];

    // Strip extractedText from documents before saving to JSONB (keep it lightweight)
    const docsToSave = briefDocuments.documents.map((d) => ({
      id: d.id,
      fileName: d.fileName,
      fileUrl: d.fileUrl,
      status: d.status,
      summary: d.summary,
      charCount: d.charCount,
    }));

    try {
      await updateAgentConfig(configId, {
        onboarding_data: {
          status: resolvedStep === 'complete' ? 'completed' : 'in_progress',
          current_step: resolvedStep,
          extraction_started: extractionRunning || extractionStatus === 'completed',
          brief_documents: { documents: docsToSave },
        },
      } as any);
    } catch (e) {
      console.error('Error saving onboarding state:', e);
    }
  };

  // Auto-save brief documents (debounced)
  useEffect(() => {
    if (status !== 'brief_questions' || !configId) return;
    const timer = setTimeout(() => {
      saveOnboardingState('brief');
    }, 3000);
    return () => clearTimeout(timer);
  }, [briefDocuments, status, configId]);

  // --- Onboarding complete ---

  const handleGoToAgent = () => {
    onSuccess();
    onClose();
    navigate(`/agents/${agentId}`);
  };

  const handleFinishLater = async () => {
    // Save current state before closing
    await saveOnboardingState();
    onSuccess();
    onClose();
  };

  // --- QR polling (unchanged) ---

  const poll = useCallback(async () => {
    if (cancelledRef.current) return;
    try {
      const result = await whatsappService.pollSessionStatus(instanceName);
      if (cancelledRef.current) return;
      switch (result.status) {
        case 'WORKING': {
          cleanup();
          const phoneNumber = result.phoneNumber || '';
          await handleConnectionSuccess(phoneNumber);
          return;
        }
        case 'SCAN_QR_CODE':
          if (result.qrBase64) setQrCode(result.qrBase64);
          break;
        case 'STARTING':
          startingCountRef.current += 1;
          // If stuck in STARTING too long, restart the session automatically
          if (startingCountRef.current >= MAX_STARTING_POLLS) {
            startingCountRef.current = 0;
            console.warn('[QRDisplayModal] Session stuck in STARTING, restarting...');
            try {
              await whatsappService.restartSession(instanceName);
            } catch (e) {
              console.error('[QRDisplayModal] Error restarting stuck session:', e);
            }
          }
          break;
        case 'FAILED':
          cleanup();
          setStatus('qr_expired');
          setErrorMessage('El código QR ha expirado o la sesión falló.');
          return;
        case 'STOPPED':
          cleanup();
          setStatus('error');
          setErrorMessage('La sesión se detuvo inesperadamente.');
          return;
      }
      if (!cancelledRef.current) {
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL);
      }
    } catch (error) {
      console.error('[QRDisplayModal] Error en polling:', error);
      if (!cancelledRef.current) {
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL);
      }
    }
  }, [instanceName, cleanup, handleConnectionSuccess]);

  useEffect(() => {
    if (!open) return;
    setQrCode(initialQR);
    setStatus('waiting');
    setErrorMessage('');
    setBusinessName('');
    setSelectedPlan('');
    setEscalationPhone('');
    setConnectedPhone('');
    setAgentId('');
    setConfigId('');
    setBriefDocuments({ documents: [] });
    setExtractionProgress(null);
    setExtractionStatus(null);
    setExtractionRunning(false);
    cancelledRef.current = false;
    startingCountRef.current = 0;
    pollTimerRef.current = setTimeout(poll, 2000);
    return cleanup;
  }, [open, instanceName, initialQR, poll, cleanup]);

  const handleRegenerateQR = async () => {
    if (retryCount >= MAX_RETRIES) {
      setStatus('error');
      setErrorMessage('Se alcanzó el límite de reintentos. Por favor, crea una nueva conexión.');
      return;
    }
    setRetryCount(prev => prev + 1);
    setStatus('waiting');
    setErrorMessage('');
    cancelledRef.current = false;
    try {
      await whatsappService.restartSession(instanceName);
      await new Promise(resolve => setTimeout(resolve, 3000));
      const result = await whatsappService.pollSessionStatus(instanceName);
      if (result.qrBase64) setQrCode(result.qrBase64);
      pollTimerRef.current = setTimeout(poll, POLL_INTERVAL);
    } catch (error) {
      console.error('Error al regenerar QR:', error);
      setStatus('error');
      setErrorMessage('Error al regenerar el código QR. Intenta de nuevo.');
    }
  };

  const handleCancel = async () => {
    cleanup();
    const safeStatuses: ConnectionStatus[] = [
      'success', 'setup_complete', 'business_setup', 'setting_up',
      'extraction_consent', 'brief_questions', 'onboarding_complete',
    ];
    if (!safeStatuses.includes(status)) {
      try {
        await whatsappService.deleteConnection(connectionId, instanceName);
      } catch (e) {
        console.error('Error cleaning up on cancel:', e);
      }
    }
    onClose();
  };

  if (!open) return null;

  // Determine if we're in onboarding phase (wider modal)
  const isOnboardingPhase = ['extraction_consent', 'brief_questions', 'onboarding_complete'].includes(status);
  const showStepper = isOnboardingPhase || status === 'setting_up';
  const stepIndex = getStepIndex(status);
  const completedSteps = getCompletedSteps(status);

  // Modal header title
  const getTitle = () => {
    switch (status) {
      case 'business_setup':
      case 'setting_up': return 'Configurar Agente';
      case 'extraction_consent': return 'Historial de Conversaciones';
      case 'brief_questions': return 'Documentos del Negocio';
      case 'onboarding_complete': return 'Configuración Completa';
      default: return 'Conectar WhatsApp';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleCancel} />
      <div className={`relative bg-white rounded-2xl shadow-lg w-[90%] p-6 transition-all duration-300 ${
        isOnboardingPhase ? 'max-w-2xl' : 'max-w-md'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg text-maxi-text-dark">
            {getTitle()}
          </h2>
          <Button variant="ghost" className="text-maxi-text-dark text-2xl" onClick={handleCancel}>
            ×
          </Button>
        </div>

        {/* Step indicator */}
        {showStepper && (
          <div className="mb-4">
            <OnboardingStepIndicator
              currentStep={stepIndex}
              steps={ONBOARDING_STEPS.map((s, i) => ({
                label: s.label,
                completed: completedSteps[i],
              }))}
            />
          </div>
        )}

        {/* Extraction progress bar (shown during brief/calendar if extraction is running) */}
        {status === 'brief_questions' && (extractionRunning || extractionStatus) && (
          <div className="mb-4">
            <ExtractionProgressBar
              progress={extractionProgress}
              status={extractionStatus}
              running={extractionRunning}
            />
          </div>
        )}

        {/* Content */}
        <div className="space-y-4">
          {/* --- QR waiting --- */}
          {status === 'waiting' && (
            <>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  <span className="text-sm text-gray-600">Esperando escaneo del código QR...</span>
                </div>
              </div>
              {qrCode && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-center">
                    <img src={qrCode} alt="Código QR de WhatsApp" className="w-64 h-64 object-contain" />
                  </div>
                </div>
              )}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-sm text-blue-900 mb-2">Instrucciones:</h3>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Abre WhatsApp en tu teléfono</li>
                  <li>Ve a Configuración &gt; Dispositivos vinculados</li>
                  <li>Toca "Vincular un dispositivo"</li>
                  <li>Escanea este código QR</li>
                </ol>
              </div>
            </>
          )}

          {/* --- Business Name Setup --- */}
          {status === 'business_setup' && (
            <div className="space-y-4">
              <div className="text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-2" />
                <p className="text-sm font-medium text-green-600 mb-1">WhatsApp conectado exitosamente</p>
                <p className="text-xs text-gray-500">+{connectedPhone}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Building2 className="w-4 h-4" />
                  <span>Nombre del negocio</span>
                </div>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Ej: Mi Empresa"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter' && businessName.trim() && escalationValid) handleSetupAgent(); }}
                />
                <p className="text-xs text-gray-500">Este nombre se usará para el agente y los flujos de trabajo.</p>

                <div className="pt-1">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                    <span>Número de escalamiento humano</span>
                  </div>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={escalationPhone}
                    onChange={(e) => setEscalationPhone(e.target.value)}
                    placeholder="Ej: 573103334444"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    onKeyDown={(e) => { if (e.key === 'Enter' && businessName.trim() && escalationValid) handleSetupAgent(); }}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Con código de país, sin <span className="font-medium">+</span> ni espacios. Es el número de WhatsApp que recibirá los avisos cuando el agente escale a un humano.
                  </p>
                  {escalationPhone.trim() !== '' && !escalationValid && (
                    <p className="text-xs text-red-500 mt-1">Ingresa un número válido (mínimo 10 dígitos, con código de país).</p>
                  )}
                </div>

                <div className="pt-1">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                    <span>Plan (opcional)</span>
                  </div>
                  <select
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Sin plan</option>
                    {PLAN_OPTIONS.map((p) => (
                      <option key={p.key} value={p.key}>{planOptionLabel(p.key)}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Puedes cambiarlo luego en la configuración del agente.</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleSkipSetup} className="flex-1">Omitir</Button>
                <Button
                  onClick={handleSetupAgent}
                  disabled={!businessName.trim() || !escalationValid}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  Configurar Agente
                </Button>
              </div>
            </div>
          )}

          {/* --- Setting up (loading) --- */}
          {status === 'setting_up' && (
            <div className="text-center py-6">
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-green-500 mb-3" />
              <p className="text-sm font-medium text-gray-700">Configurando agente...</p>
              <p className="text-xs text-gray-500 mt-1">Creando flujos de trabajo y vinculando canal</p>
            </div>
          )}

          {/* --- Extraction Consent --- */}
          {status === 'extraction_consent' && (
            <div className="space-y-4">
              <div className="text-center">
                <MessageSquareText className="w-12 h-12 mx-auto text-blue-500 mb-2" />
                <h3 className="text-sm font-semibold mb-1">Extraer Historial de WhatsApp</h3>
                <p className="text-xs text-gray-500 max-w-sm mx-auto">
                  Podemos analizar tus conversaciones existentes para entrenar al agente
                  con el estilo de comunicación de tu negocio.
                </p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs text-blue-800">
                  Se extraerán los chats recientes, incluyendo transcripción de audios
                  y descripción de imágenes. Este proceso puede tomar unos minutos.
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleSkipExtraction} className="flex-1">
                  Omitir
                </Button>
                <Button
                  onClick={startExtraction}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <MessageSquareText className="w-4 h-4 mr-2" />
                  Extraer Historial
                </Button>
              </div>
            </div>
          )}

          {/* --- Brief / Document Upload --- */}
          {status === 'brief_questions' && (
            <div className="space-y-4">
              <BriefDocumentUpload
                documents={briefDocuments.documents}
                onChange={(docs) => setBriefDocuments({ documents: docs })}
              />
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={handleFinishLater} className="flex-1">
                  Terminar después
                </Button>
                <Button onClick={handleBriefNext} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                  Finalizar
                  <Check className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* --- Onboarding Complete --- */}
          {status === 'onboarding_complete' && (
            <div className="space-y-4">
              <div className="text-center mb-2">
                <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-2" />
                <p className="text-lg font-semibold text-green-600">Configuración completada</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <SummaryItem label="Conexión WhatsApp" completed />
                <SummaryItem label="Nombre del negocio" completed value={businessName} />
                {escalationDigits && <SummaryItem label="Escalamiento humano" completed value={escalationDigits} />}
                <SummaryItem
                  label="Extracción de historial"
                  completed={extractionStatus === 'completed'}
                  running={extractionRunning}
                  skipped={!extractionStatus}
                />
                <SummaryItem label="Documentos del negocio" completed={briefDocuments.documents.some((d) => d.status === 'done')} />
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Ahora puedes ir a la configuración del agente para generar el prompt con IA.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleFinishLater} className="flex-1">
                  Cerrar
                </Button>
                <Button onClick={handleGoToAgent} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                  Ir a Configurar Agente
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* --- Legacy statuses --- */}
          {status === 'setup_complete' && (
            <div className="text-center py-4">
              <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-2" />
              <p className="text-lg font-semibold text-green-600">Agente configurado</p>
              <p className="text-sm text-gray-600 mt-2">Flujos de trabajo creados y canal vinculado correctamente</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-4">
              <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-2" />
              <p className="text-lg font-semibold text-green-600">¡Conectado exitosamente!</p>
              <p className="text-sm text-gray-600 mt-2">Tu WhatsApp ha sido vinculado correctamente</p>
            </div>
          )}

          {/* --- Error --- */}
          {status === 'error' && (
            <div className="text-center py-4">
              <XCircle className="w-16 h-16 mx-auto text-red-500 mb-2" />
              <p className="text-lg font-semibold text-red-600">Error</p>
              <p className="text-sm text-gray-600 mt-2">{errorMessage}</p>
            </div>
          )}

          {/* --- QR Expired --- */}
          {status === 'qr_expired' && (
            <div className="text-center py-4">
              <RefreshCw className="w-16 h-16 mx-auto text-orange-500 mb-2" />
              <p className="text-lg font-semibold text-orange-600">Código QR expirado</p>
              <p className="text-sm text-gray-600 mt-2">{errorMessage}</p>
              {retryCount < MAX_RETRIES && (
                <Button variant="outline" onClick={handleRegenerateQR} className="mt-4">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerar QR ({MAX_RETRIES - retryCount} intentos restantes)
                </Button>
              )}
              {retryCount >= MAX_RETRIES && (
                <p className="text-xs text-red-500 mt-2">
                  Se alcanzó el límite de reintentos. Cierra e intenta crear una nueva conexión.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {(status === 'waiting' || status === 'error' || status === 'qr_expired') && (
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={handleCancel}>Cancelar</Button>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Summary item for onboarding complete ---

function SummaryItem({ label, completed, running, skipped, value }: {
  label: string;
  completed?: boolean;
  running?: boolean;
  skipped?: boolean;
  value?: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {completed ? (
        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
      ) : running ? (
        <Loader2 className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
      ) : skipped ? (
        <Minus className="w-4 h-4 text-gray-400 flex-shrink-0" />
      ) : (
        <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
      )}
      <span className={completed ? 'text-foreground' : 'text-muted-foreground'}>
        {label}
      </span>
      {value && <span className="text-xs text-muted-foreground ml-auto">{value}</span>}
    </div>
  );
}
